import {
  accountGet,
  accountPost,
  accountDelete,
  accountPut
} from './prefixedUrls.js';
import axios from './axios.setup.js';

/**
 * @typedef {Object} Tag
 * @property {number} id
 * @property {number} account_id
 * @property {string} name
 * @property {string} father_id
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} WithChildren
 * @property {TagTree[]} children
 */

/**
 * @typedef {Tag & WithChildren} TagTree
 * @property {TagTree[]} children
 */

export async function getAll() {
  let res = await axios.get('/tags');
  let tags = res.data;
  tags.sort((a,b) => {
    let aname = a.name.toLowerCase();
    let bname = b.name.toLowerCase();
    if (aname < bname) return -1;
    if (aname > bname) return 1;
    return 0;
  })
  return tags;
}
 
/**
 * Get all tags for a specific account
 * @param {string} accountId The account uuid
 * @returns {Promise<Tag[]>}
 */
export async function getAccountTags(accountId) {
  let res = await accountGet('tags', accountId);
  let tags = res.data;
  tags.sort((a,b) => {
    let aname = a.name.toLowerCase();
    let bname = b.name.toLowerCase();
    if (aname < bname) return -1;
    if (aname > bname) return 1;
    return 0;
  })
  return tags;
}

/**
 * Get all tags for a specific account as a tree.
 * @param {string} accountId The account uuid
 * @returns {Promise<TagTree[]>}
 */
export async function getAllAsTrees(accountId) {
  let tags = await getAccountTags(accountId);
  return tagListToTree(tags);
}

/**
 * Get a tag by its id (and account id)
 * @param {string} accountId The account uuid
 * @param {string} id
 * @returns {Promise<Tag>}
 */
export async function get(accountId, id) {
  let res = await accountGet(`tags/${id}`, accountId);
  return res.data;
}

/**
 * Get the number of messages in the tag
 * @param {string} accountId The account uuid
 * @param {string} id
 */
export async function messageCount(accountId, id) {
  let res = await accountGet(`tags/${id}/message-count`, accountId);
  return res.data;
}

/**
 * Search an account tags by name of parent id
 * @param {string} accountId The account uuid
 * @param {object} filters
 * @param {string} filters.name
 * @param {string} filters.father_id
 * @returns {Promise<Tag[]>}
 */
export async function search(accountId, filters) {
  let nameFilter = filters?.name ?? null;
  let fatherFilter = filters?.father_id ?? null;
  let params = {};

  if (nameFilter) {
    params['name'] = nameFilter;
  }

  if (fatherFilter) {
    params['father_id'] = fatherFilter;
  }

  let res = await accountGet('tags', accountId, params);

  return res.data;
}

/**
 * Creates a new tag for the specified account.
 *
 * @param {string} accountId The account uuid
 * @param {string} name
 * @param {string | null} father_id
 * @returns {Promise<Tag>}
 */
export async function create(accountId, name, father_id) {
  let tag = {
    name
  };

  if (father_id)
    tag.father_id = father_id;

  let res = await accountPost('tags', accountId, tag);

  return res.data;
}

/**
 * Edit a tag for the specified account
 * @param {string} accountId The account uuid
 * @param {Tag} tag
 * @returns {Promise<Tag>}
 */
export async function update(accountId, tag) {
  let res = await accountPut(`tags/${tag.id}`, accountId, tag)
  return res.data;
}

/**
 * Removes a tag
 *
 * @param {string} accountId The account uuid
 * @param {string} tagId
 * @returns {Promise<null>}
 */
export async function remove(accountId, tagId) {
  let res = await accountDelete(`tags/${tagId}`, accountId);
  return res.data;
}

/**
 * Attaches a tag to a message
 *
 * @param {string} accountId The account uuid
 * @param {number} tagId
 * @param {number} messageId
 */
export async function attach(accountId, tagId, messageId) {
  let res = await accountPost(`tags/${tagId}/messages/${messageId}`, accountId, {})
  return res.data;
}

/**
 * Detaches a tag from a message
 *
 * @param {string} accountId The account uuid
 * @param {number} tagId
 * @param {number} messageId
 */
export async function detach(accountId, tagId, messageId) {
  let res = await accountDelete(`tags/${tagId}/messages/${messageId}`, accountId, {});
  return res.data;
}

/**
 * Set the message tags to a list of specific tags
 * 
 * @param {string} accountId The account uuid
 * @param {string} messageId The message uuid
 * @param {number[]} tags A list of tag ids
 */
export async function sync(accountId, messageId, tags) {
  let res = await accountPut(`messages/${messageId}/tags`, accountId, { tags });
  return res.data;
}

/**
 * Transforms a list of tags to a list of tag trees.
 *
 * @param {Tag[]} tags
 * @returns {TagTree[]}
 */
export function tagListToTree(tags) {
  /** @type {Map<number, TagTree>} */
  let tagMap = new Map();
  let tagTrees = tags.map((t) => ({...t, children: []}));

  for (let tag of tagTrees) {
    tagMap.set(tag.id, tag);
  }

  for (let tag of tagTrees) {
    if (tag.father_id) {
      let father = tagMap.get(tag.father_id);

      if (!father) {
        tag.father_id = null;
      } else {
        father.children.push(tag);
      }
    }
  }

  return tagTrees.filter((t) => !t.father_id);
}

/**
 * Flattens out a list of tag trees, adding a "display" key
 * with the modified name (it adds a '-' to indicate the nesting)
 * @param {TagTree[]} tagTrees
 * @param {boolean} pathInLabel
 * @returns {Tag[]}
 */
export function flattenTagTrees(tagTrees, pathInLabel = false) {
  return tagTrees.flatMap(t => flattenTagTree(t, 0, pathInLabel));
}

function flattenTagTree(tagTree, prefix, pathInLabel) {
  let tag = {
    ...tagTree,
    children: []
  };

  let children = tagTree.children;

  tag.display = prefix
    ? prefix + ' ' + tag.name
    : tag.name
  
  // Prepare prefix for the node children
  prefix = (pathInLabel)
    ? tag.display + ' >'
    : prefix + '-'
  
  return [
    tag,
    ...(children.flatMap(c => flattenTagTree(c, prefix, pathInLabel)))
  ];
}
